import { APP_VERSION } from '../lib/version.js'
import { useEffect, useRef, useState } from 'react'
import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import { useMobile, useBackAction } from '../platform/mobile.js'
import { auth, logout, api } from '../lib/api.js'
import { startChangeFeed, stopChangeFeed, onChange } from '../lib/changes.js'
import { startPresence } from '../lib/presence.js'
import { unlockAudio, playAlert } from '../lib/beep.js'
import ChangePassword from './ChangePassword.jsx'
import MyReplies from './MyReplies.jsx'
import { DialogHost, alertBox } from './Dialog.jsx'
import { useFollowUps, shouldAutoOpen, FollowUpPanel } from './FollowUps.jsx'

// 图标：模块级常量，必须在用到它的组件之前声明（no-use-before-define 已开成 error，
// 2026-10-13 工作台白屏就是变量提前引用引起的暂时性死区）
const Ico = ({ d }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
    strokeLinecap="round" strokeLinejoin="round"><path d={d} /></svg>
)

export default function Layout() {
  const nav = useNavigate()
  const agent = auth.agent
  const [online, setOnline] = useState(false)
  const [showPw, setShowPw] = useState(false)
  const [showReplies, setShowReplies] = useState(false)
  const [menu, setMenu] = useState(false)   // 左下「设置」菜单：我的话术、修改密码、退出登录
  const [wide, setWide] = useState(false)   // 左栏展开显示功能名字；默认收起只显示图标（2026-10-04 Gavin 定）
  // 移动端（≤767px，手机 Safari / 安卓 App 壳）：左栏变成可收起的抽屉，默认收起；
  // 安卓 App 的物理返回键在抽屉开着时先关抽屉，不直接退出 App（2026-10-05 Gavin 要）
  const mobile = useMobile()
  const [mobileNav, setMobileNav] = useState(false)
  useBackAction(mobile && mobileNav, () => setMobileNav(false), 50)
  // 接单开关：跟心跳在线是两回事。吃饭/开会时关掉，系统就不再派新单给你，
  // 手上没成交的单也会转给其他在班的人（已被定性锁住的除外）。
  const [accepting, setAccepting] = useState(true)
  const [dutyBusy, setDutyBusy] = useState(false)
  // 客户发来但本人还没看过的消息数。放在侧边栏是为了在别的页面（会话记录、统计）
  // 也能看见有新消息，不用来回切回工作台确认。
  const [unread, setUnread] = useState(0)
  // 跟进提醒（喇叭）：角标 = 今天还没联系的（逾期 + 今天到期）；运营每天第一次打开自动弹一次
  const followUps = useFollowUps(agent?.id)
  const [showFollowUps, setShowFollowUps] = useState(false)
  useEffect(() => {
    if (shouldAutoOpen(followUps, agent?.id)) setShowFollowUps(true)
  }, [followUps, agent?.id])
  // 未读数比上一次变多了（真的来了新消息，不是标记已读导致变少）才响一声提示音；
  // null 表示还没拿到过第一次数字，第一次不响（2026-10-06 Gavin 要求）
  const prevUnreadRef = useRef(null)
  const applyUnread = (n) => {
    if (typeof n !== 'number') return
    if (prevUnreadRef.current !== null && n > prevUnreadRef.current) playAlert()
    prevUnreadRef.current = n
    setUnread(n)
  }

  // 提示音要先有一次用户点击才能响（浏览器规则），页面随便点一下就提前解锁
  useEffect(() => {
    const unlock = () => { unlockAudio(); document.removeEventListener('click', unlock) }
    document.addEventListener('click', unlock)
    return () => document.removeEventListener('click', unlock)
  }, [])

  // 登录期间整个页签只挂一条「有没有变化」的长轮询（见 lib/changes.js），各页面都从它取信号
  useEffect(() => {
    if (!agent?.id) return
    startChangeFeed({ presence: true })
    return () => stopChangeFeed()
  }, [agent?.id])

  useEffect(() => {
    if (!agent?.id) return
    let alive = true
    const tick = () => api.countUnread()
      .then(r => alive && applyUnread(r.unread || 0))
      .catch(() => {})   // 角标失败不该打扰使用，静默
    tick()
    // 客户来消息、会话关闭这类变化后端会立刻通知；60 秒兜底一次，防通知丢了角标一直不准
    const offChange = onChange(tick)
    const timer = setInterval(tick, 60 * 1000)
    // 工作台标记已读后会广播最新数字，据此立刻更新，不用干等下一轮轮询
    const onChanged = e => {
      if (alive && typeof e.detail === 'number') applyUnread(e.detail)
    }
    window.addEventListener('unread-changed', onChanged)
    return () => {
      alive = false
      offChange()
      clearInterval(timer)
      window.removeEventListener('unread-changed', onChanged)
    }
  }, [agent?.id])

  // 接单状态以服务端为准：原来固定初始成「接单中」，暂离后一刷新页面就显示错
  useEffect(() => {
    if (!agent?.id) return
    api.listAgents()
      .then(list => { const me = list.find(a => a.id === agent.id); if (me) setAccepting(me.accepting !== false) })
      .catch(() => {})
  }, [agent?.id])

  async function toggleDuty() {
    setDutyBusy(true)
    try {
      const r = await api.setAccepting(agent.id, !accepting)
      setAccepting(r.accepting)
      if (!r.accepting && r.handed) alertBox(`已暂离，${r.handed} 个等人工的客户已转给其他在班同事（AI 接待中的仍归你）`)
    } catch (e) { alertBox(e.message) } finally { setDutyBusy(false) }
  }

  // 在线状态靠心跳判活（后端 150 秒无心跳即视为离线）。
  // 不用 beforeunload 标记离线：刷新页面时它会触发，且请求常被浏览器取消或延迟送达，
  // 与新页面的上线请求打架，导致明明在线却显示离线。
  useEffect(() => {
    if (!agent?.id) return
    return startPresence({ heartbeat: () => api.setOnline(agent.id, true), publish: setOnline })
  }, [agent?.id])

  // 点外面任意处收起设置菜单
  useEffect(() => {
    if (!menu) return
    const close = () => setMenu(false)
    window.addEventListener('click', close)
    return () => window.removeEventListener('click', close)
  }, [menu])

  // 退出登录 = 下班：先关接单（跟点「暂离」一样，手上未成交的转给其他在班同事），再下线。
  // 下次登录默认是暂离，点头像才开始接单（2026-09-29 Gavin 定）
  async function doLogout() {
    stopChangeFeed()
    if (agent?.id) await api.setAccepting(agent.id, false).catch(() => {})
    if (agent?.id) await api.setOnline(agent.id, false).catch(() => {})
    await logout()
    nav('/login', { replace: true })
  }

  const linkClass = ({ isActive }) => `rail-btn${isActive ? ' on' : ''}`
  // 导航整屏居中；屏幕矮时往上让，至少和底部那组按钮（约 120px 高）隔开 16px，不叠在一起。
  // 管理员 6 个、运营 3 个，高度分开算（按钮 50 + 间距 16，见 index.css .rail-btn）
  const navCount = auth.isAdmin ? 7 : auth.isAftersales ? 1 : 4
  const navH = navCount * 50 + (navCount - 1) * 16
  const navTop = `min(calc(50% - ${navH / 2}px), calc(100% - ${120 + 16 + navH}px))`

  return (
    <div className="app-layout" style={{ display: 'flex', height: '100vh' }}>
      {/* 手机端顶栏：汉堡按钮打开左栏抽屉，右边放接单状态（桌面端不显示，见 mobile.css .mobile-only） */}
      <header className="mobile-only mobile-topbar">
        <button className="icon-button" title="导航" aria-label="打开导航" onClick={() => setMobileNav(true)}>
          <Ico d="M4 7h16M4 12h16M4 17h16" />
        </button>
        <strong>ETC运营系统{APP_VERSION && <small className="muted" style={{ marginLeft: 6, fontWeight: 400 }}>{APP_VERSION}</small>}</strong>
        <div className="mobile-duty">
          {followUps && <FollowUpButton data={followUps} onClick={() => setShowFollowUps(true)} />}
          <span className="mobile-duty-label">{!online ? '离线' : accepting ? '接单中' : '暂离'}</span>
          <button className={`rail-me ${!online ? 'off' : accepting ? 'on' : 'away'}`}
            title={accepting ? '暂离' : '开始接单'} aria-label={accepting ? '暂离' : '开始接单'}
            onClick={toggleDuty} disabled={dutyBusy}>
            {(agent?.name || agent?.id || '?').slice(-1)}<span className="rail-dot" />
          </button>
        </div>
      </header>
      {mobile && mobileNav && <button className="rail-backdrop" aria-label="关闭导航" onClick={() => setMobileNav(false)} />}

      {/* 图标栏：只放图标省地方，鼠标放上去才显示名字（data-tip）；手机端展开成带文字的抽屉 */}
      <aside className={`rail${wide || mobile ? ' wide' : ''}${mobileNav ? ' mobile-open' : ''}`}>
        <div className="rail-head">
          <div className="rail-logo" data-tip={`ETC 运营系统 ${APP_VERSION}`.trim()}>ETC</div>
          <button className="rail-toggle" onClick={() => setWide(v => !v)} aria-label={wide ? '收起左栏' : '展开左栏'}>
            <Ico d={wide ? 'M15 6l-6 6 6 6' : 'M9 6l6 6-6 6'} />
          </button>
          <button className="icon-button mobile-only" title="关闭导航" aria-label="关闭导航" onClick={() => setMobileNav(false)}>
            <Ico d="M6 6l12 12M18 6L6 18" />
          </button>
        </div>

        {/* 导航图标整屏上下居中（屏幕矮时自动上移，见 navTop）；手机端点了链接就收起抽屉 */}
        <nav className="rail-nav" style={{ top: navTop }} onClick={e => { if (e.target.closest('a')) setMobileNav(false) }}>
          <NavLink to="/workbench" className={linkClass}
            data-tip={unread > 0 ? `工作台 · ${unread} 条客户消息还没看` : '工作台'}>
            <Ico d="M21 12a8.5 8.5 0 0 1-12.6 7.4L3 21l1.6-5.1A8.5 8.5 0 1 1 21 12z" />
            {(wide || mobile) && <span className="rail-label">工作台</span>}
            {unread > 0 && <span className="rail-badge">{unread > 99 ? '99+' : unread}</span>}
          </NavLink>
          {followUps && <FollowUpButton data={followUps} label={wide || mobile}
            onClick={() => { setMobileNav(false); setShowFollowUps(true) }} />}
          {!auth.isAftersales && <NavLink to="/records" className={linkClass} data-tip="会话记录">
            <Ico d="M12 8v4l3 2M3.05 11a9 9 0 1 1 .5 4M3 4v5h5" />
            {(wide || mobile) && <span className="rail-label">会话记录</span>}
          </NavLink>}
          {!auth.isAftersales && <NavLink to="/stats" className={linkClass} data-tip="数据统计">
            <Ico d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
            {(wide || mobile) && <span className="rail-label">数据统计</span>}
          </NavLink>}
          {auth.isAdmin && (
            <NavLink to="/monitor" className={linkClass} data-tip="监控大屏">
              <Ico d="M3 4h18v12H3zM8 20h8M12 16v4M6 12l3-3 3 2 5-5" />
              {(wide || mobile) && <span className="rail-label">监控大屏</span>}
            </NavLink>
          )}
          {auth.isAdmin && (
            <NavLink to="/knowledge" className={linkClass} data-tip="知识库">
              <Ico d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20V3H6.5A2.5 2.5 0 0 0 4 5.5v14zM4 19.5A2.5 2.5 0 0 0 6.5 22H20v-5" />
              {(wide || mobile) && <span className="rail-label">知识库</span>}
            </NavLink>
          )}
          {auth.isAdmin && (
            <NavLink to="/settings" className={linkClass} data-tip="设置中心">
              <Ico d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-2.9 1.2V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-2.9-1.2l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0-1.2-2.9H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.2-2.9l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 2.9-1.2V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 2.9 1.2l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0 1.2 2.9H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
              {(wide || mobile) && <span className="rail-label">设置中心</span>}
            </NavLink>
          )}
        </nav>

        <div className="rail-foot">
          <div style={{ position: 'relative' }}>
            <button className={`rail-btn${menu ? ' on' : ''}`} data-tip={menu ? undefined : '设置'}
              onClick={e => { e.stopPropagation(); setMenu(v => !v) }}>
              <Ico d="M5 9h14M5 15h14" />
              {(wide || mobile) && <span className="rail-label">设置</span>}
            </button>
            {menu && (
              <div className="rail-menu" onClick={e => e.stopPropagation()}>
                <button onClick={() => { setMenu(false); setShowReplies(true) }}>我的话术</button>
                <button onClick={() => { setMenu(false); setShowPw(true) }}>修改密码</button>
                <button className="danger" onClick={doLogout} title="退出后自动暂离，不再给你派单">退出登录</button>
              </div>
            )}
          </div>
          {/* 头像兼接单开关：点一下在「接单中 / 暂离」之间切换，外圈和小点颜色就是当前状态 */}
          <button className={`rail-me ${!online ? 'off' : accepting ? 'on' : 'away'}`} onClick={toggleDuty} disabled={dutyBusy}
            data-tip={`${agent?.name || agent?.id}${auth.isAdmin ? ' · 管理员' : auth.isAftersales ? ' · 售后' : ''} · ${dutyBusy ? '处理中…'
              : !online ? '离线' : accepting ? '接单中 · 点击暂离（不再派新单，手上等人工的转给其他在班同事，AI 接待中的留在你名下）' : '暂离 · 点击开始接单'}`}>
            {(agent?.name || agent?.id || '?').slice(-1)}
            <span className="rail-dot" />
          </button>
        </div>
      </aside>

      <main className="app-main" style={{ flex: 1, overflow: 'auto' }}>
        {/* 在线/接单状态传给页面，工作台的宠物卡片跟着显示 */}
        <Outlet context={{ online, accepting }} />
      </main>

      {showPw && <ChangePassword onClose={() => setShowPw(false)} />}
      {showReplies && <MyReplies onClose={() => setShowReplies(false)} />}
      {showFollowUps && followUps && <FollowUpPanel data={followUps} onClose={() => setShowFollowUps(false)} />}
      <DialogHost />
    </div>
  )
}

function FollowUpButton({ data, label, onClick }) {
  const n = data.pending
  return (
    <button className="rail-btn" onClick={onClick}
      data-tip={n > 0 ? `跟进提醒 · 今天还有 ${n} 个未进线客户要联系` : '跟进提醒'}>
      <Ico d="M3 11l18-5v12L3 14v-3zM11.6 16.8a3 3 0 1 1-5.8-1.6" />
      {label && <span className="rail-label">跟进提醒</span>}
      {n > 0 && <span className="rail-badge">{n > 99 ? '99+' : n}</span>}
    </button>
  )
}
